import React, { useEffect, useState } from 'react';
import { Modal, Button, Alert, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileContract, faCheckCircle, faExternalLinkAlt, faRedo } from '@fortawesome/free-solid-svg-icons';
import axiosInstance from 'helpers/api';
import { toast } from 'react-toastify';

interface MakeFillableModalProps {
  show: boolean;
  onHide: () => void;
  documentId: string;
  documentName: string;
  onConversionComplete?: (payload: {
    template_id: string;
    fillable_form_id: string;
    edit_url: string;
  }) => void;
}

interface FillableInfoResponse {
  success: boolean;
  is_fillable: boolean;
  template_id?: string;
  fillable_form_id?: string;
  edit_url?: string;
  message?: string;
}

const MakeFillableModal: React.FC<MakeFillableModalProps> = ({
  show,
  onHide,
  documentId,
  documentName,
  onConversionComplete
}) => {
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [converting, setConverting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fillableInfo, setFillableInfo] = useState<FillableInfoResponse | null>(null);
  const [conversionComplete, setConversionComplete] = useState(false);

  useEffect(() => {
    if (!show || !documentId) {
      return;
    }

    // Reset modal state whenever a new document opens
    setError(null);
    setFillableInfo(null);
    setConversionComplete(false);
    setCheckingStatus(true);

    const fetchFillableInfo = async () => {
      try {
        const response = await axiosInstance.get<FillableInfoResponse>(`/documents/${documentId}/fillable-info`);
        if (response.data?.success && response.data.is_fillable) {
          setFillableInfo(response.data);
          setConversionComplete(true);
        }
      } catch (err: any) {
        // 404 or not fillable yet is fine; suppress logging unless unexpected
        const status = err.response?.status;
        if (status && status !== 404) {
          console.error('Error fetching PDFFiller status:', err);
          setError(err.response?.data?.error || err.message || 'Failed to load fillable status');
        }
      } finally {
        setCheckingStatus(false);
      }
    };

    fetchFillableInfo();
  }, [show, documentId]);

  const handleConvert = async () => {
    try {
      setConverting(true);
      setError(null);
      const response = await axiosInstance.post(`/documents/${documentId}/make-fillable`, {});

      if (response.data?.success) {
        const payload = {
          template_id: response.data.template_id,
          fillable_form_id: response.data.fillable_form_id,
          edit_url: response.data.edit_url
        };
        setFillableInfo({ success: true, is_fillable: true, ...payload });
        setConversionComplete(true);
        toast.success('Document converted to a PDFFiller template.');
        if (onConversionComplete) {
          onConversionComplete(payload);
        }
      } else {
        throw new Error(response.data?.error || 'Unknown error converting document');
      }
    } catch (err: any) {
      console.error('Error converting document to PDFFiller template:', err);
      const message = err.response?.data?.details || err.response?.data?.error || err.message || 'Failed to convert document';
      setError(message);
      toast.error('Failed to convert document to a fillable template.');
    } finally {
      setConverting(false);
    }
  };

  const renderBody = () => {
    if (checkingStatus && !conversionComplete) {
      return (
        <div className="text-center">
          <Spinner animation="border" className="mb-3" />
          <p className="mb-0">Checking current PDFFiller status...</p>
        </div>
      );
    }

    if (conversionComplete && fillableInfo?.is_fillable) {
      return (
        <Alert variant="success" className="text-center">
          <FontAwesomeIcon icon={faCheckCircle} size="3x" className="mb-3" />
          <h5>PDFFiller Template Ready</h5>
          <p className="mb-1">Template ID: {fillableInfo.template_id}</p>
          <p className="mb-3">Fillable Form ID: {fillableInfo.fillable_form_id}</p>
          {fillableInfo.edit_url && (
            <Button
              variant="primary"
              href={fillableInfo.edit_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              <FontAwesomeIcon icon={faExternalLinkAlt} className="me-2" />
              Open in PDFFiller
            </Button>
          )}
        </Alert>
      );
    }

    return (
      <>
        <Alert variant="info">
          <FontAwesomeIcon icon={faFileContract} className="me-2" />
          Convert this PDF into a PDFFiller template so agents can complete it digitally.
          <ul className="mb-0 mt-2">
            <li>Document downloads from Kairos S3 and uploads to PDFFiller</li>
            <li>PDFFiller returns an editable template link</li>
            <li>All template IDs are saved back to Kairos for reuse</li>
          </ul>
        </Alert>

        <p><strong>Document:</strong> {documentName}</p>

        {error && (
          <Alert variant="danger">
            {error}
          </Alert>
        )}
      </>
    );
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <FontAwesomeIcon icon={faFileContract} className="me-2" />
          Make Fillable with PDFFiller
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {renderBody()}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={converting}>
          Close
        </Button>
        {!conversionComplete && (
          <Button
            variant="success"
            onClick={handleConvert}
            disabled={converting}
          >
            {converting ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Sending to PDFFiller...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faRedo} className="me-2" />
                Convert Now
              </>
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default MakeFillableModal;
